/**
 * The live body of an extruded Text: one mesh under the text root, built from
 * the face's glyph outlines (`textBodyGeometry.ts`, loaded with the outlines
 * the first time a Text extrudes, so flat text never fetches the tessellator).
 *
 * The flat glyphs stay the face, so this only adds the solid behind them, in
 * the fill colour (the stroke colour when stroked) on a lit material. Export bakes the same geometry and
 * material ({@link createTextBodyMaterial}) to the node's `src`.
 */

import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { Material } from '@babylonjs/core/Materials/material';
import { Color3, type Color4 } from '@babylonjs/core/Maths/math.color';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';
import { Logger } from '../../utils/Logger';
import type { BodyFrame, BodyGeometry, BodyGlyph, GlyphOutlines } from './textBodyGeometry';

/** Ends the live body mesh's name. Exporters skip it: the export bakes the body from the Text. */
export const TEXT_BODY_SUFFIX = '__textBody';

/** What a body is built from. */
export interface TextBodyInput {
    outlineUrl: string;
    glyphs: BodyGlyph[];
    frame: BodyFrame;
}

/** A built body, as the export bakes it. */
export interface BuiltTextBody {
    geometry: BodyGeometry;
    /** The fill colour, or the stroke colour when stroked; opacity applied. */
    color: Color4;
}

// Per page, for its lifetime: one face's outlines are 40-200 KB, fetched once
// however many Texts draw in it. A failed fetch is forgotten, so the next
// build retries.
const outlines = new Map<string, Promise<GlyphOutlines | null>>();

function loadOutlines(url: string): Promise<GlyphOutlines | null> {
    let pending = outlines.get(url);
    if (!pending) {
        pending = fetch(url)
            .then(async (response) => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                return await response.json() as GlyphOutlines;
            })
            .catch((error: unknown) => {
                Logger.warn(`[Text] glyph outlines failed to load (${String(error)}): ${url}`);
                outlines.delete(url);
                return null;
            });
        outlines.set(url, pending);
    }
    return pending;
}

/**
 * The body's material: the fill colour, lit, in the linear space glTF stores.
 *
 * The geometry winds counter-clockwise outward, as glTF does. Babylon gives a
 * mesh in a right-handed scene the opposite front face unless told, which
 * culled the outside walls live and wrote the bake inside out.
 */
export function createTextBodyMaterial(name: string, scene: Scene, color: Color4): PBRMaterial {
    const material = new PBRMaterial(name, scene);
    material.sideOrientation = Material.CounterClockWiseSideOrientation;
    material.metallic = 0;
    material.roughness = 0.6;
    setBodyColor(material, color);
    return material;
}

function setBodyColor(material: PBRMaterial, color: Color4): void {
    material.albedoColor = new Color3(color.r, color.g, color.b).toLinearSpace();
    material.alpha = color.a;
    material.transparencyMode = color.a < 1 ? Material.MATERIAL_ALPHABLEND : Material.MATERIAL_OPAQUE;
}

export class TextBody {
    private _mesh: Mesh | null = null;
    private _material: PBRMaterial | null = null;
    private _geometry: BodyGeometry | null = null;
    private _color: Color4 | null = null;
    private _parent: TransformNode | null = null;
    private _generation = 0;
    private _pending: Promise<void> = Promise.resolve();

    constructor(private readonly _scene: Scene, private readonly _name: string) {}

    /**
     * Build the body for `input`, or remove it when `input` is null. The latest
     * call wins. Resolves false when the build failed (outlines that did not
     * load, a builder chunk that did not, or a build that threw): the text is
     * then flat, and the same input is worth trying again.
     */
    update(input: TextBodyInput | null): Promise<boolean> {
        const generation = ++this._generation;
        if (!input) {
            this._apply(null);
            this._pending = Promise.resolve();
            return Promise.resolve(true);
        }
        const built = Promise.all([loadOutlines(input.outlineUrl), import('./textBodyGeometry')])
            .then(([loaded, { buildTextBodyGeometry }]) => {
                if (generation === this._generation) this._apply(loaded ? buildTextBodyGeometry(loaded, input.glyphs, input.frame) : null);
                return loaded !== null;
            })
            .catch((error: unknown) => {
                // A failed chunk or a build that throws leaves the text flat, not a save aborted.
                Logger.warn(`[Text] "${this._name}" body failed to build, drawing flat: ${String(error)}`);
                if (generation === this._generation) this._apply(null);
                return false;
            });
        this._pending = built.then(() => undefined);
        return built;
    }

    setColor(color: Color4): void {
        this._color = color.clone();
        if (this._material) setBodyColor(this._material, this._color);
    }

    setParent(parent: TransformNode | null): void {
        this._parent = parent;
        if (this._mesh) this._mesh.parent = parent;
    }

    /** The body once every pending build has landed; null when there is none. */
    async built(): Promise<BuiltTextBody | null> {
        let pending: Promise<void>;
        do {
            pending = this._pending;
            await pending;
        } while (pending !== this._pending);
        return this._geometry && this._color ? { geometry: this._geometry, color: this._color.clone() } : null;
    }

    dispose(): void {
        this._generation++;
        this._apply(null);
    }

    private _apply(geometry: BodyGeometry | null): void {
        this._geometry = geometry;
        if (!geometry) {
            this._mesh?.dispose(false, true);
            this._mesh = null;
            this._material = null;
            return;
        }
        if (!this._mesh || this._mesh.isDisposed()) {
            this._mesh = new Mesh(`${this._name}${TEXT_BODY_SUFFIX}`, this._scene);
            this._mesh.isPickable = false;
            this._material = createTextBodyMaterial(`${this._name}${TEXT_BODY_SUFFIX}`, this._scene, this._color ?? new Color3(1, 1, 1).toColor4());
            this._mesh.material = this._material;
        }
        const data = new VertexData();
        data.positions = geometry.positions;
        data.normals = geometry.normals;
        data.indices = geometry.indices;
        data.applyToMesh(this._mesh, true);
        this._mesh.parent = this._parent;
    }
}
